import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { Card, Col, Divider, Row, Typography } from 'antd';
import { FileTextOutlined, TableOutlined } from '@ant-design/icons';

import { useGetMemberId, useGetProductFullView, useGridView } from 'hooks';
import { useProductValidation } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useValidateWizardForm';

import {
  FormButton,
  StyledModal,
  UploadFile,
} from 'common/components';
import ErrorModal from 'common/components/product-publication-setup/ErrorModal';

import WizardContentBox from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardContentBox';
import { WizardDescription } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardDescription';
import WizardTitle from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardTitle';
import TemplateSubmissionSteps from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/TemplateSubmissionSteps';

import useWizard from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/useWizard';
import useGetWizardFormItems from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useGetWizardFormItems';

import { MODAL_TYPES } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/config/stepModalConstants';

import { UPLOAD_TEMPLATE_WIZARD } from 'services/product/endpoints';

import { UPLOAD_MAX_SIZE } from 'static/Constants';

import {
  transformWizardItems,
  mapWizardFormItemsToProducts,
  hydrateProductsWithHierarchy,
  deriveAnswersFromResponse,
  autoPopulateCaseGtin,
  ANSWER_KEYS,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';

import {
  applyValidationResult,
  propagateUpcErrorsToShipperItems,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/validation';

import { sleep } from 'utils/delay';
import 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/styles/SubmissionMethod.less';

const ProductGridQuestionStep = () => {
  const { actions, state } = useWizard();
  const { id } = useParams();

  const { memberId } = useGetMemberId();

  const { productFull } = useGetProductFullView({
    productId: id,
    enabled: Boolean(id),
  });

  const { validateWizardForm } = useProductValidation(state, actions);

  const { detailItems: selectedProducts } = useGridView();
  const isDetailView = Boolean(id);

  const selectedItems = isDetailView ? [productFull] : selectedProducts;

  const { getWizardFormItems } = useGetWizardFormItems();

  const handleOpenSubmissionGrid = async () => {
    actions.openModal(MODAL_TYPES.ONLINE_GRID);
    actions.setGridLoading(true);

    try {
      const productIds = selectedItems.map(
        (p) => p?.id || p?.productId || p?.productItemId
      );

      const response = await getWizardFormItems({ productIds });
      const derivedAnswers = deriveAnswersFromResponse(
        response?.data
      );
      // Reset ALL known answer keys (absent ones default to false) so
      // initial-state `true` values don't bleed into the grid as "Yes".
      const fullAnswers = Object.fromEntries(
        ANSWER_KEYS.map((key) => [key, derivedAnswers[key] ?? false])
      );
      actions.mergeAnswers(fullAnswers);

      const wizardFormItems = response?.data?.wizardFormItems ?? [];
      let updatedProducts =
        mapWizardFormItemsToProducts(wizardFormItems);

      updatedProducts = hydrateProductsWithHierarchy(updatedProducts);

      const validateResponse = await validateWizardForm({
        products: updatedProducts,
        memberId,
      });

      updatedProducts = applyValidationResult({
        response: validateResponse,
        products: updatedProducts,
      });

      actions.setProducts(updatedProducts);
    } finally {
      actions.setGridLoading(false);
    }
  };

  return (
    <WizardContentBox className='wizard-submission-method'>
      <WizardTitle title='Choose a Product Submission Method:' />
      <Divider />
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card className='wizard-submission-method__card'>
            <div className='wizard-submission-method__heading'>
              <span className='wizard-submission-method__icon'>
                <FileTextOutlined />
              </span>
              <Typography.Title level={5}>Template</Typography.Title>
            </div>
            <WizardDescription>
              To use a template to build your item(s) in the Product Submission Grid:
            </WizardDescription>
            <TemplateSubmissionSteps />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className='wizard-submission-method__card'>
            <div className='wizard-submission-method__heading'>
              <span className='wizard-submission-method__icon'>
                <TableOutlined />
              </span>
              <Typography.Title level={5}>Product Submission Grid</Typography.Title>
            </div>
            <WizardDescription>
              To manually build your item(s) in the online Product Submission Grid:
            </WizardDescription>
            <FormButton
              text='Product Submission Grid'
              icon={<TableOutlined />}
              className='wizard-submission-method__grid-button'
              onClick={handleOpenSubmissionGrid}
            />
          </Card>
        </Col>
      </Row>
    </WizardContentBox>
  );
};

export const LoadTemplateModal = ({
  visible,
  onCancel,
  onValidateWizardForm,
  memberId,
  gridRef,
}) => {
  const { actions } = useWizard();
  const [errorList, setErrorList] = useState([]);

  const handleTriggerRefreshGrid = async () => {
    await sleep();
    const api = gridRef.current?.api;
    if (!api) return;
    api.refreshCells({ force: true });
  };

  const handleResponseUpload = async (response) => {
    if (response?.data) {
      if (response?.data?.isSuccess) {
        let wizardFormItems = response?.data?.data?.wizardFormItems ?? [];
        wizardFormItems =
          transformWizardItems(wizardFormItems).map(autoPopulateCaseGtin);
        wizardFormItems = hydrateProductsWithHierarchy(wizardFormItems);

        // Derive answer flags from the loaded data so conditional columns
        // (merchandisedInTray, perishable, etc.) show correctly even though
        // the user skipped the question flow.
        const derivedAnswers = deriveAnswersFromResponse(response?.data?.data);
        // Reset ALL known answer keys (absent ones default to false) so
        // initial-state `true` values don't bleed into the grid as "Yes".
        const fullAnswers = Object.fromEntries(
          ANSWER_KEYS.map((key) => [key, derivedAnswers[key] ?? false])
        );

        actions.mergeAnswers(fullAnswers);
        actions.setProducts(wizardFormItems);
        actions.openModal(MODAL_TYPES.ONLINE_GRID);
        actions.setGridLoading(true);
        onCancel();

        try {
          const responseValidation = await onValidateWizardForm({
            products: wizardFormItems,
            memberId,
          });

          wizardFormItems = applyValidationResult({
            response: responseValidation,
            products: wizardFormItems,
          });
          wizardFormItems = propagateUpcErrorsToShipperItems(wizardFormItems);

          actions.setProducts(wizardFormItems);

          await handleTriggerRefreshGrid();
        } finally {
          actions.setGridLoading(false);
        }
      } else {
        const errors = response?.data?.errors?.length
          ? response.data.errors
          : (response?.data?.message || '').split('\n').filter(Boolean);
        setErrorList(errors);
      }
    } else {
      const errors = (response?.message || '').split('\n').filter(Boolean);
      setErrorList(errors);
    }
  };

  return (
    <StyledModal
      width={'50vw'}
      bodyStyle={{ height: '50vh' }}
      title='Upload Template Product'
      visible={visible}
      onCancel={onCancel}
      closable={true}
      maskClosable={false}
      footer={null}
      destroyOnClose
    >
      <div className='upload-advanced-file__modal'>
        <UploadFile
          uploadAPL={true}
          manualUpload={true}
          isBase64={false}
          showMultiple={false}
          showXlsTemplate={false}
          apiUrl={UPLOAD_TEMPLATE_WIZARD}
          nameFormData='File'
          showUploadSuccessMsg={false}
          showServerMessage={false}
          notShowMessage={true}
          getResponse={(response) => handleResponseUpload(response)}
          maxSize={UPLOAD_MAX_SIZE.GENERAL}
          supportTypes={['xlsx', 'xls']}
        />
        <div className='upload-advanced-file__modal--error-text'>
          {errorList && errorList.length > 0 && (
            <ErrorModal
              errorList={errorList}
              title={'Upload File Errors'}
              showAll={errorList?.length > 1}
            />
          )}
        </div>
      </div>
    </StyledModal>
  );
};

export default ProductGridQuestionStep;
